import { formatDistanceToNow } from "date-fns";
import { UserName } from "@/components/badges/UserName";
import { UserCheck, UserX, Loader2, Inbox } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useReceivedConnectionRequests,
  useAcceptConnection,
  useDeclineConnection,
} from "@/hooks/useCommunityConnections";

export function ReceivedRequestsList() {
  const { data: requests, isLoading } = useReceivedConnectionRequests();
  const acceptConnection = useAcceptConnection();
  const declineConnection = useDeclineConnection();

  const count = requests?.length || 0;

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (count === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Inbox className="h-4 w-4" />
            Connection Requests
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-4">No new requests</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/20">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Inbox className="h-4 w-4 text-primary" />
          Connection Requests
          <Badge variant="default" className="ml-auto">
            {count}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {requests?.map((request) => {
          const requester = request.requester;
          if (!requester) return null;

          const isPending = acceptConnection.isPending || declineConnection.isPending;
          const isAccepting = acceptConnection.isPending && acceptConnection.variables?.connectionId === request.id;
          const isDeclining = declineConnection.isPending && declineConnection.variables === request.id;

          return (
            <div
              key={request.id}
              className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-muted/50 rounded-lg border border-primary/10"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Avatar className="h-10 w-10 shrink-0">
                  <AvatarImage src={requester.avatar_url || undefined} />
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {requester.full_name?.charAt(0) || "?"}
                  </AvatarFallback>
                </Avatar>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate"><UserName userId={requester.id} name={requester.full_name} /></p>
                    <Badge
                      variant="outline"
                      className={
                        requester.role === "mentor" ? "badge-mentor shrink-0" : "badge-entrepreneur shrink-0"
                      }
                    >
                      {requester.role === "mentor" ? "Mentor" : "Entrepreneur"}
                    </Badge>
                  </div>
                  {requester.bio && (
                    <p className="text-sm text-muted-foreground line-clamp-1">{requester.bio}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Received {formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <Button
                  size="sm"
                  onClick={() =>
                    acceptConnection.mutate({
                      connectionId: request.id,
                      requesterName: requester.full_name,
                    })
                  }
                  disabled={isPending}
                >
                  {isAccepting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <UserCheck className="h-4 w-4 mr-1" />
                      Accept
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => declineConnection.mutate(request.id)}
                  disabled={isPending}
                >
                  {isDeclining ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <UserX className="h-4 w-4 mr-1" />
                      Decline
                    </>
                  )}
                </Button>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

export default ReceivedRequestsList;
